import styled from '@emotion/styled';
import { graphql } from 'gatsby';
import React from 'react';

import { theme } from '@kindur/components';

import Layout from '../components/Common/Layout';
import SEO from '../components/Common/SEO';
import Toolbar from '../components/Common/Toolbar';
import BeInTheKnow from '../components/Home/BeInTheKnow';
import FooterAboutUs from '../components/Home/FooterAboutUs';
import FooterLegalese from '../components/Home/FooterLegalese';
import Hero from '../components/Home/Hero';
import SmartDraw from '../components/Home/SmartDraw';
import SmartDrawDifference from '../components/Home/SmartDrawDifference';
import SmartDrawInvest from '../components/Home/SmartDrawInvest';
import SmartDrawPaycheck from '../components/Home/SmartDrawPaycheck';
import SocialProof from '../components/Home/SocialProof';

import invest from '../images/smartdraw/invest.png';
import paycheck from '../images/smartdraw/paycheck.png';

import * as Content from '../content/new-homepage';

import colors from '../styles/colors';

const FooterGradient = styled('footer')`
  background-color: ${colors.DarkPurple};
  color: #fff;
  padding: 4rem 0 6rem;

  div {
    max-width: 41rem;
  }

  div:last-of-type .border-top {
    border-top: 1px solid ${colors.Turquoise100};
    ${theme.breakpoints.tabletDown} {
      padding: 0 24px;
    }
  }
`;

export interface HomeProps {
  data: any;
  location: any;
  pageContext: any;
}

export interface HomeState {
  scrollHandler: any;
  showToolbar: boolean;
}

const TOOLBAR_ANCHOR = `hero-bullets-container`;

class Home extends React.Component<HomeProps, HomeState> {
  constructor(props) {
    super(props);
    this.state = {
      scrollHandler: undefined,
      showToolbar: false
    };
  }

  public componentWillMount(): void {
    this.attachScrollHandler(true);
  }

  public componentWillUnmount(): void {
    this.attachScrollHandler(false);
  }

  public attachScrollHandler(doit: boolean): void {
    if (typeof window === 'undefined') {
      return;
    }
    if (doit) {
      window.addEventListener('scroll', this.handleScrollPosition);
    } else {
      cancelAnimationFrame(this.state.scrollHandler);
      window.removeEventListener('scroll', this.handleScrollPosition);
    }
  }

  public handleScrollPosition = () => {
    cancelAnimationFrame(this.state.scrollHandler);
    const setState = this.setState.bind(this);
    setState({
      scrollHandler: requestAnimationFrame(() => {
        // @ts-ignore
        const { innerHeight, scrollY } = window;
        const boundary = document.getElementById(TOOLBAR_ANCHOR);

        // Once the TOOLBAR_ANCHOR appears, show the Toolbar
        if (!this.state.showToolbar && boundary && scrollY > boundary.offsetTop - innerHeight) {
          setState({ showToolbar: true });
        } else if (this.state.showToolbar && scrollY < 10) {
          setState({ showToolbar: false });
        }
      })
    });
  }

  public render() {
    const { data, location, pageContext } = this.props;

    return (
      <Layout location={location} pageContext={pageContext}>
        <SEO
          title={'SmartDraw - Retirement Planning and Tools at Kindur'}
          description={`Kindur helps you earn thousands in retirement savings through powerful tax strategies and withdrawal recommendations.`}
        />
        <Hero
          alt={'Woman smiling while holding a coffee mug.'}
          content={Content.Hero}
          location={location}
          image={data.heroImage.childImageSharp.fluid}
        />

        <SmartDrawDifference
          content={Content.SmartDrawDifference}
        />
        <SmartDraw content={Content.SmartDraw} image={data.smartDraw.childImageSharp.fluid} />
        <SmartDrawInvest
          content={Content.SmartDrawInvest}
          image={invest}
        />
        <SmartDrawPaycheck
          content={Content.SmartDrawPaycheck}
          image={paycheck}
        />
        <SocialProof content={Content.SocialProof} />
        <BeInTheKnow content={Content.BeInTheKnow} />
        <FooterGradient>
          <FooterAboutUs content={Content.Footer.about} />
          <FooterLegalese content={Content.Footer.legalese} />
        </FooterGradient>
        <Toolbar
          cta={{
            className: `ld-homepage`,
            href: `https://app.kindur.com/register/`,
            text: `Start a Free Plan`
          }}
          location={location}
          visible={this.state.showToolbar}
        />
      </Layout>
    );
  }
}

export const pageQuery = graphql`
  query {
    heroImage: file(relativePath: { eq: "home/prod-shot_hp-hero-income.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1152) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
    contactDavid: file(relativePath: { eq: "home/david.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 320) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
    smartDraw: file(relativePath: { eq: "home/prod-shot_hp-smartdraw.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 640) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
    smartDrawInvest: file(relativePath: { eq: "home/prod-shot_invest-holdings.png" }) {
      childImageSharp {
        fluid(maxWidth: 640) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
    smartDrawPaycheck: file(relativePath: { eq: "home/prod-shot_paycheck-lifestyle.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 640) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
    smartDrawTechnology: file(relativePath: { eq: "home/graph_hp-earnings.png" }) {
      childImageSharp {
        fluid(maxWidth: 640) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
  }
`;

export default Home;
